import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Observable, forkJoin } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { AuthService } from '../auth.service'
import { CashflowDto } from 'src/app/types/dto/cashflow.dto'
import { environment } from 'src/environments/environment'
import { HttpHelperService } from '../http.service'
import { LogService } from '../log.service'
import { RecalcCompletionForecast } from 'src/app/types/dto/recalc-completion-forecast.dto'
import { ForecastTaskData } from 'src/app/types/dto/forecast-task-data.dto'
import { ForecastJobData } from 'src/app/types/dto/forecast-job-data.dto'
import { TimedCache } from '../timed-cache'
import { JobLookupApiService } from './job-lookup.api.service'

@Injectable({
  providedIn: 'root',
})
export class JobTaskForecastsService {
  private httpHelper = inject(HttpHelperService);
  private auth = inject(AuthService);
  private logger = inject(LogService);
  private http = inject(HttpClient);
  private jobLookup = inject(JobLookupApiService);

  private cachedCashflow = new TimedCache<CashflowDto[]>()
  private cachedRecalc = new TimedCache<RecalcCompletionForecast[]>()

  constructor() {
    this.httpHelper.clearCaches.subscribe(() => {
      this.cachedCashflow.clear()
      this.cachedRecalc.clear()
    })
  }

  getCashflow(): Observable<CashflowDto[]> {
    const comp = this.auth.getCurrentCompany()
    if (!comp) {
      this.logger.log('job-task-forecasts.service', 'getCashflow', null, 'no company')
      throw Error('no company set in getCashflow')
    }

    return this.cachedCashflow.get(comp.id, () =>
      this.http.get<CashflowDto[]>(
        environment.apiUrl + 'job-task-forecasts/cashflow',
        this.httpHelper.httpOptions
      ).pipe(switchMap((cashflow) => this.jobLookup.enrich(cashflow)))
    )
  }

  getRecalcCompletion(): Observable<RecalcCompletionForecast[]> {
    const comp = this.auth.getCurrentCompany()
    if (!comp) {
      this.logger.log('job-task-forecasts.service', 'getRecalcCompletion', null, 'no company')
      throw Error('No company set in getRecalcCompletion')
    }

    return this.cachedRecalc.get(comp.id, () => this.buildRecalcCompletionObservable())
  }

  private buildRecalcCompletionObservable(): Observable<RecalcCompletionForecast[]> {
    const forecastTaskData = this.getRecalcCompletionData()
    const forecastJobData = this.getForecastJobData()

    return forkJoin([forecastTaskData, forecastJobData]).pipe(
      map(([tasks, jobs]) => {
        // Create a Map for O(1) job lookups by jobId
        const jobMap = new Map<number, ForecastJobData>()
        jobs.forEach((job) => {
          jobMap.set(job.jobId, job)
        })

        // Build results by iterating tasks once and looking up jobs
        const recalcCompletion: RecalcCompletionForecast[] = []

        tasks.forEach((task) => {
          const job = jobMap.get(task.jobId)
          if (job) {
            recalcCompletion.push({
              id: `${task.id}-${job.jobId}`,
              jobId: job.jobId,
              jobNumber: job.jobNumber,
              taskMasterId: task.taskMasterId,
              taskTitle: task.taskTitle,
              forecastStartDate: task.forecastStartDate,
              forecastCompletionDate: task.forecastCompletionDate,
              targetStartDate: task.targetStartDate,
              targetCompletionDate: task.targetCompletionDate,
              siteManager: job.siteManager,
              division: job.division,
              stage: job.stage,
              houseType: job.houseType,
              contractSignedDate: job.contractSignedDate
            })
          }
        })

        return recalcCompletion
      }),
      switchMap((recalcCompletion) => this.jobLookup.enrich(recalcCompletion))
    )
  }

  /**
   * Returns existing forecasts, calculating and persisting missing job forecasts as needed.
   * This PATCH can also create job-extra records or update their forecast completion date.
   * There should only be one of each task type per job.
   */
  private getRecalcCompletionData() {
    const url = environment.apiUrl + 'job-task-forecasts/recalc-completion?standardJobsOnly=true'

    const obs$ = this.http.patch<ForecastTaskData[]>(url, null, this.httpHelper.httpOptions)

    return obs$
  }

  private getForecastJobData() {
    const url = environment.apiUrl + 'job-task-forecasts/forecast-job-data?standardJobsOnly=true'

    const obs$ = this.http.get<ForecastJobData[]>(url, this.httpHelper.httpReadOnlyOptions)

    return obs$
  }
}
